"use client";

import { useMemo, useState, type ReactNode } from "react";
import {
  BookOpenTextIcon,
  BrainIcon,
  ChartNoAxesColumnIncreasingIcon,
  CircleHelpIcon,
  CircleStopIcon,
  CompassIcon,
  DownloadIcon,
  EraserIcon,
  FileSearchIcon,
  FileTextIcon,
  GlobeIcon,
  ListTodoIcon,
  PencilLineIcon,
  SearchIcon,
  SparklesIcon,
  TagsIcon,
  TerminalIcon
} from "lucide-react";
import { useAuiState } from "@assistant-ui/react";
import {
  getAggregateStatus,
  isToolActivityPart,
  ToolActivityDetail,
  ToolActivityRow,
  type ToolActivity,
  type ToolActivityPart,
  type ToolActivityStatus
} from "@/agents-v2/components/assistant-ui/tool-activity-row";
import { useThreadStalled } from "@/agents-v2/components/assistant-ui/stalled-run";
import {
  getRetrievalSources,
  RetrievalSourcesDropdown
} from "@/agents-v2/components/common/retrieval-sources";
import { BID_EVALUATION_RESULTS_TOOL_NAME } from "@/agents-v2/components/tool-ui/bid-evaluation-results-tool-ui";
import {
  BID_NAVIGATION_TOOL_NAME,
  parseBidNavigationRequest
} from "@/agents-v2/components/tool-ui/bid-agent-navigation-tool-ui";
import { EXPORT_ARTIFACT_TOOL_NAME } from "@/agents-v2/components/common/export-artifact-tool-name";
import {
  COMPLETE_BID_CREATION_TOOL_NAME,
  REQUEST_BID_TEAM_TOOL_NAME
} from "@/agents-v2/components/tool-ui/bid-creation-tool-ui";
import { ASK_QUESTION_TOOL_NAME } from "@/agents-v2/questions/question-responses";
import { WRITE_TODOS_TOOL_NAME } from "@/agents-v2/components/assistant-ui/thread-todos";
import {
  isMemoryActivityPart,
  MemoryActivityGroup
} from "@/agents-v2/memory/activity/memory-activity";
import {
  FORGET_TOOL_NAME,
  GET_ONTOLOGY_TOOL_NAME,
  LIST_MEMORIES_TOOL_NAME,
  RECALL_TOOL_NAME,
  REMEMBER_TOOL_NAME,
  REVISE_TOOL_NAME,
  SET_BID_VALUES_TOOL_NAME,
  UPDATE_ONTOLOGY_TOOL_NAME,
  USE_MEMORIES_TOOL_NAME
} from "@/agents-v2/memory/tool-names";
import {
  asRecord,
  getStringValue,
  parseMaybeJson
} from "@/agents-v2/utils/unknown";

const CONTENT_LIBRARY_TOOL = "content_library_search";
const WEB_SEARCH_TOOL = "external_research_search";
const ORGANISATION_GUIDELINES_TOOL = "check_organisation_guidelines";
const TASK_TOOL = "task";
const GENERAL_PURPOSE_SUBAGENT = "general-purpose";
const START_ASYNC_TASK_TOOL = "start_async_task";
const CHECK_ASYNC_TASK_TOOL = "check_async_task";
const UPDATE_ASYNC_TASK_TOOL = "update_async_task";
const CANCEL_ASYNC_TASK_TOOL = "cancel_async_task";
const LIST_ASYNC_TASKS_TOOL = "list_async_tasks";
const SHOW_BID_PROGRESS_TOOL = "show_bid_progress";

// Tool activities intentionally hidden from the streamed activity list.
// - check_organisation_guidelines: the agent runs this on almost every turn, so
//   the "Checking organizational guidelines..." row is repetitive noise that
//   adds no signal for the user. See MYT-1782.
// - write_todos: the plan now renders as an inline card (ThreadPlan), so a
//   separate "Updated plan" row would just duplicate it. Background workers
//   keep checklists too, and there "Updated plan" under "Insights" read as a
//   stalled step (PE-322) - their rows skip it via isHiddenActivityTool.
// - check/list_async_tasks: pure supervisor bookkeeping — the background
//   workers already show live in their own progress widgets, so "checked
//   progress" rows would narrate machinery the user can see directly.
// - show_bid_progress: renders as the setup progress card (its own tool UI),
//   so an activity row would duplicate it.
const HIDDEN_ACTIVITY_TOOL_NAMES = new Set<string>([
  ORGANISATION_GUIDELINES_TOOL,
  WRITE_TODOS_TOOL_NAME,
  CHECK_ASYNC_TASK_TOOL,
  LIST_ASYNC_TASKS_TOOL,
  SHOW_BID_PROGRESS_TOOL
]);

/**
 * Whether a tool's activity is never narrated to the user - bookkeeping and
 * per-turn noise (see HIDDEN_ACTIVITY_TOOL_NAMES). Shared with the
 * background-worker rows so both surfaces stay silent on the same tools.
 * @param toolName - The tool name as it appears on the tool call.
 * @returns True when the activity should not be shown.
 */
export const isHiddenActivityTool = (toolName: string): boolean =>
  HIDDEN_ACTIVITY_TOOL_NAMES.has(toolName);

const ACTIVITY_BY_TOOL_NAME = {
  [RECALL_TOOL_NAME]: {
    key: "memory-recall",
    runningLabel: "Looking through memory...",
    completeLabel: "Looked through memory",
    errorLabel: "Couldn't look through memory",
    icon: BrainIcon
  },
  [LIST_MEMORIES_TOOL_NAME]: {
    key: "memory-list",
    runningLabel: "Reviewing memories...",
    completeLabel: "Reviewed memories",
    errorLabel: "Couldn't review memories",
    icon: BrainIcon
  },
  [GET_ONTOLOGY_TOOL_NAME]: {
    key: "memory-ontology",
    runningLabel: "Checking how bids are classified...",
    completeLabel: "Checked how bids are classified",
    errorLabel: "Couldn't check how bids are classified",
    icon: TagsIcon
  },
  [UPDATE_ONTOLOGY_TOOL_NAME]: {
    key: "memory-ontology-update",
    runningLabel: "Changing how bids are classified...",
    completeLabel: "Changed how bids are classified",
    errorLabel: "Couldn't change how bids are classified",
    icon: TagsIcon
  },
  [START_ASYNC_TASK_TOOL]: {
    key: "background-work-start",
    runningLabel: "Starting background work...",
    completeLabel: "Started background work",
    errorLabel: "Couldn't start background work",
    icon: SparklesIcon
  },
  [UPDATE_ASYNC_TASK_TOOL]: {
    key: "background-work-update",
    runningLabel: "Passing on your decision...",
    completeLabel: "Passed on your decision",
    errorLabel: "Couldn't pass on your decision",
    icon: PencilLineIcon
  },
  [CANCEL_ASYNC_TASK_TOOL]: {
    key: "background-work-cancel",
    runningLabel: "Stopping background work...",
    completeLabel: "Stopped background work",
    errorLabel: "Couldn't stop background work",
    icon: CircleStopIcon
  },
  // Bid-creation persist/generate tools: the chat renders their cards, but
  // the background-worker widgets show them as activity lines too.
  get_company_profile: {
    key: "company-profile",
    runningLabel: "Reading your company profile...",
    completeLabel: "Read your company profile",
    errorLabel: "Couldn't read your company profile",
    icon: FileTextIcon
  },
  generate_tender_summary: {
    key: "tender-summary",
    runningLabel: "Summarising the tender...",
    completeLabel: "Summarised the tender",
    errorLabel: "Couldn't summarise the tender",
    icon: FileTextIcon
  },
  generate_win_themes: {
    key: "win-themes",
    runningLabel: "Drafting win themes...",
    completeLabel: "Drafted win themes",
    errorLabel: "Couldn't draft win themes",
    icon: SparklesIcon
  },
  set_bid_insights: {
    key: "save-insights",
    runningLabel: "Saving insights...",
    completeLabel: "Saved insights",
    errorLabel: "Couldn't save insights",
    icon: SparklesIcon
  },
  save_bid_outline: {
    key: "save-outline",
    runningLabel: "Saving the outline...",
    completeLabel: "Saved the outline",
    errorLabel: "Couldn't save the outline",
    icon: ListTodoIcon
  },
  save_section_writing_plan: {
    key: "save-writing-plan",
    runningLabel: "Saving the writing plan...",
    completeLabel: "Saved the writing plan",
    errorLabel: "Couldn't save the writing plan",
    icon: ListTodoIcon
  },
  draft_section_answer: {
    key: "draft-section-answer",
    runningLabel: "Drafting the answer...",
    completeLabel: "Drafted the answer",
    errorLabel: "Couldn't draft the answer",
    icon: PencilLineIcon
  },
  [USE_MEMORIES_TOOL_NAME]: {
    key: "memory-use",
    runningLabel: "Using memory...",
    completeLabel: "Used memory",
    errorLabel: "Couldn't use memory",
    icon: BrainIcon
  },
  [REMEMBER_TOOL_NAME]: {
    key: "memory-remember",
    runningLabel: "Remembering...",
    completeLabel: "Remembered",
    errorLabel: "Couldn't remember that",
    icon: BrainIcon
  },
  [REVISE_TOOL_NAME]: {
    key: "memory-revise",
    runningLabel: "Updating a memory...",
    completeLabel: "Updated a memory",
    errorLabel: "Couldn't update that memory",
    icon: PencilLineIcon
  },
  [FORGET_TOOL_NAME]: {
    key: "memory-forget",
    runningLabel: "Forgetting...",
    completeLabel: "Forgot a memory",
    errorLabel: "Couldn't forget that memory",
    icon: EraserIcon
  },
  [SET_BID_VALUES_TOOL_NAME]: {
    key: "memory-bid-values",
    runningLabel: "Classifying this bid...",
    completeLabel: "Classified this bid",
    errorLabel: "Couldn't classify this bid",
    icon: TagsIcon
  },
  [CONTENT_LIBRARY_TOOL]: {
    key: "content-library",
    runningLabel: "Searching your library...",
    completeLabel: "Searched your library",
    errorLabel: "Couldn't search your library",
    icon: BookOpenTextIcon
  },
  [WEB_SEARCH_TOOL]: {
    key: "web-search",
    runningLabel: "Searching the web...",
    completeLabel: "Searched the web",
    errorLabel: "Couldn't search the web",
    icon: GlobeIcon
  },
  [ORGANISATION_GUIDELINES_TOOL]: {
    key: "organisation-guidelines",
    runningLabel: "Checking organizational guidelines...",
    completeLabel: "Checked organizational guidelines",
    errorLabel: "Couldn't check organizational guidelines",
    icon: FileSearchIcon
  },
  [EXPORT_ARTIFACT_TOOL_NAME]: {
    key: "download",
    runningLabel: "Preparing downloads...",
    completeLabel: "Prepared downloads",
    errorLabel: "Couldn't prepare downloads",
    icon: DownloadIcon
  },
  [BID_EVALUATION_RESULTS_TOOL_NAME]: {
    key: "bid-evaluation",
    runningLabel: "Presenting evaluation results...",
    completeLabel: "Presented evaluation results",
    errorLabel: "Couldn't present evaluation results",
    icon: ChartNoAxesColumnIncreasingIcon
  },
  [BID_NAVIGATION_TOOL_NAME]: {
    key: "bid-navigation",
    runningLabel: "Navigating...",
    completeLabel: "Navigated",
    errorLabel: "Couldn't navigate",
    icon: CompassIcon
  },
  [REQUEST_BID_TEAM_TOOL_NAME]: {
    key: "bid-team-picker",
    runningLabel: "Preparing bid team picker...",
    completeLabel: "Prepared bid team picker",
    errorLabel: "Couldn't prepare bid team picker",
    icon: CircleHelpIcon
  },
  [COMPLETE_BID_CREATION_TOOL_NAME]: {
    key: "bid-creation-complete",
    runningLabel: "Finishing bid setup...",
    completeLabel: "Finished bid setup",
    errorLabel: "Couldn't finish bid setup",
    icon: SparklesIcon
  },
  [ASK_QUESTION_TOOL_NAME]: {
    key: "questions",
    runningLabel: "Preparing questions...",
    completeLabel: "Prepared questions",
    errorLabel: "Couldn't prepare questions",
    icon: CircleHelpIcon
  },
  get_bid_overview: {
    key: "bid-overview",
    runningLabel: "Reading bid overview...",
    completeLabel: "Read bid overview",
    errorLabel: "Couldn't read bid overview",
    icon: FileTextIcon
  },
  get_bid_sections: {
    key: "bid-sections",
    runningLabel: "Reading bid sections...",
    completeLabel: "Read bid sections",
    errorLabel: "Couldn't read bid sections",
    icon: FileTextIcon
  },
  search_bid_answers: {
    key: "bid-answer-search",
    runningLabel: "Searching bid answers...",
    completeLabel: "Searched bid answers",
    errorLabel: "Couldn't search bid answers",
    icon: SearchIcon
  },
  get_storyboard_context: {
    key: "storyboard-context",
    runningLabel: "Reading bid context...",
    completeLabel: "Read bid context",
    errorLabel: "Couldn't read bid context",
    icon: FileTextIcon
  },
  get_evaluation_context: {
    key: "evaluation-context",
    runningLabel: "Reading evaluation context...",
    completeLabel: "Read evaluation context",
    errorLabel: "Couldn't read evaluation context",
    icon: ChartNoAxesColumnIncreasingIcon
  },
  get_compliance_context: {
    key: "compliance-context",
    runningLabel: "Reading compliance context...",
    completeLabel: "Read compliance context",
    errorLabel: "Couldn't read compliance context",
    icon: FileSearchIcon
  },
  search_tender_documents: {
    key: "tender-document-search",
    runningLabel: "Searching tender documents...",
    completeLabel: "Searched tender documents",
    errorLabel: "Couldn't search tender documents",
    icon: FileSearchIcon
  },
  search_outline_sources: {
    key: "outline-source-search",
    runningLabel: "Searching outline sources...",
    completeLabel: "Searched outline sources",
    errorLabel: "Couldn't search outline sources",
    icon: SearchIcon
  },
  list_bid_users: {
    key: "bid-users",
    runningLabel: "Checking bid users...",
    completeLabel: "Checked bid users",
    errorLabel: "Couldn't check bid users",
    icon: FileSearchIcon
  },
  run_outline_evaluation: {
    key: "outline-evaluation",
    runningLabel: "Evaluating bid sections...",
    completeLabel: "Evaluated bid sections",
    errorLabel: "Couldn't evaluate bid sections",
    icon: ChartNoAxesColumnIncreasingIcon
  },
  run_compliance_evaluation: {
    key: "compliance-evaluation",
    runningLabel: "Evaluating compliance...",
    completeLabel: "Evaluated compliance",
    errorLabel: "Couldn't evaluate compliance",
    icon: ChartNoAxesColumnIncreasingIcon
  },
  get_outline_evaluation_results: {
    key: "outline-evaluation-results",
    runningLabel: "Reading evaluation results...",
    completeLabel: "Read evaluation results",
    errorLabel: "Couldn't read evaluation results",
    icon: ChartNoAxesColumnIncreasingIcon
  },
  get_compliance_evaluation_results: {
    key: "compliance-evaluation-results",
    runningLabel: "Reading compliance results...",
    completeLabel: "Read compliance results",
    errorLabel: "Couldn't read compliance results",
    icon: ChartNoAxesColumnIncreasingIcon
  },
  generate_outline_evaluation_summaries: {
    key: "outline-evaluation-summaries",
    runningLabel: "Summarizing evaluation results...",
    completeLabel: "Summarized evaluation results",
    errorLabel: "Couldn't summarize evaluation results",
    icon: ChartNoAxesColumnIncreasingIcon
  },
  generate_compliance_evaluation_summaries: {
    key: "compliance-evaluation-summaries",
    runningLabel: "Summarizing compliance results...",
    completeLabel: "Summarized compliance results",
    errorLabel: "Couldn't summarize compliance results",
    icon: ChartNoAxesColumnIncreasingIcon
  },
  suggest_outline_changes: {
    key: "outline-suggestions",
    runningLabel: "Preparing outline suggestions...",
    completeLabel: "Prepared outline suggestions",
    errorLabel: "Couldn't prepare outline suggestions",
    icon: ListTodoIcon
  },
  suggest_bid_metadata_updates: {
    key: "bid-update-suggestions",
    runningLabel: "Preparing bid update suggestions...",
    completeLabel: "Prepared bid update suggestions",
    errorLabel: "Couldn't prepare bid update suggestions",
    icon: PencilLineIcon
  },
  suggest_node_insertion: {
    key: "content-insertion-suggestion",
    runningLabel: "Preparing content suggestions...",
    completeLabel: "Prepared content suggestions",
    errorLabel: "Couldn't prepare content suggestions",
    icon: PencilLineIcon
  },
  suggest_image_generation: {
    key: "image-suggestion",
    runningLabel: "Generating image suggestion...",
    completeLabel: "Generated image suggestion",
    errorLabel: "Couldn't generate image suggestion",
    icon: SparklesIcon
  },
  select_relevant_storyboard: {
    key: "section-storyboard",
    runningLabel: "Reading the section storyboard...",
    completeLabel: "Read the section storyboard",
    errorLabel: "Couldn't read the section storyboard",
    icon: FileTextIcon
  },
  check_grounding: {
    key: "grounding-check",
    runningLabel: "Checking the draft against your sources...",
    completeLabel: "Checked the draft against your sources",
    errorLabel: "Couldn't check the draft against your sources",
    icon: FileSearchIcon
  },
  check_word_count: {
    key: "word-count-check",
    runningLabel: "Checking the word count...",
    completeLabel: "Checked the word count",
    errorLabel: "Couldn't check the word count",
    icon: ListTodoIcon
  },
  suggest_inline_revision: {
    key: "inline-revision-suggestion",
    runningLabel: "Preparing text revisions...",
    completeLabel: "Prepared text revisions",
    errorLabel: "Couldn't prepare text revisions",
    icon: PencilLineIcon
  },
  suggest_node_revision: {
    key: "node-revision-suggestion",
    runningLabel: "Preparing content revisions...",
    completeLabel: "Prepared content revisions",
    errorLabel: "Couldn't prepare content revisions",
    icon: PencilLineIcon
  },
  suggest_node_deletion: {
    key: "deletion-suggestion",
    runningLabel: "Preparing deletion suggestion...",
    completeLabel: "Prepared deletion suggestion",
    errorLabel: "Couldn't prepare deletion suggestion",
    icon: PencilLineIcon
  },
  suggest_rewrite: {
    key: "rewrite-suggestion",
    runningLabel: "Preparing rewrite...",
    completeLabel: "Prepared rewrite",
    errorLabel: "Couldn't prepare rewrite",
    icon: PencilLineIcon
  },
  delete_suggestion: {
    key: "delete-suggestion",
    runningLabel: "Updating suggestions...",
    completeLabel: "Updated suggestions",
    errorLabel: "Couldn't update suggestions",
    icon: PencilLineIcon
  },
  write_todos: {
    key: "planning",
    runningLabel: "Updating plan...",
    completeLabel: "Updated plan",
    errorLabel: "Couldn't update plan",
    icon: ListTodoIcon
  },
  [TASK_TOOL]: {
    key: "helper-agent",
    runningLabel: "Running a helper agent...",
    completeLabel: "Finished helper agent",
    errorLabel: "Couldn't finish helper agent",
    icon: SparklesIcon
  },
  read_file: {
    key: "file-read",
    runningLabel: "Reading files...",
    completeLabel: "Read files",
    errorLabel: "Couldn't read files",
    icon: FileTextIcon
  },
  write_file: {
    key: "file-update",
    runningLabel: "Updating files...",
    completeLabel: "Updated files",
    errorLabel: "Couldn't update files",
    icon: PencilLineIcon
  },
  edit_file: {
    key: "file-update",
    runningLabel: "Updating files...",
    completeLabel: "Updated files",
    errorLabel: "Couldn't update files",
    icon: PencilLineIcon
  },
  ls: {
    key: "file-browse",
    runningLabel: "Checking files...",
    completeLabel: "Checked files",
    errorLabel: "Couldn't check files",
    icon: FileSearchIcon
  },
  glob: {
    key: "file-browse",
    runningLabel: "Checking files...",
    completeLabel: "Checked files",
    errorLabel: "Couldn't check files",
    icon: FileSearchIcon
  },
  grep: {
    key: "file-search",
    runningLabel: "Searching files...",
    completeLabel: "Searched files",
    errorLabel: "Couldn't search files",
    icon: SearchIcon
  },
  execute: {
    key: "analysis",
    runningLabel: "Running analysis...",
    completeLabel: "Ran analysis",
    errorLabel: "Couldn't run analysis",
    icon: TerminalIcon
  },
  // ---- Word add-in document tools (pane-specific; not in the web app) ----
  read_document_range: {
    key: "word-read",
    runningLabel: "Reading the document...",
    completeLabel: "Read the document",
    errorLabel: "Couldn't read the document",
    icon: FileTextIcon
  },
  search_document: {
    key: "word-search",
    runningLabel: "Searching the document...",
    completeLabel: "Searched the document",
    errorLabel: "Couldn't search the document",
    icon: FileSearchIcon
  },
  edit_document: {
    key: "word-edit",
    runningLabel: "Editing the document...",
    completeLabel: "Edited the document",
    errorLabel: "Couldn't edit the document",
    icon: PencilLineIcon
  },
  add_document_comment: {
    key: "word-comment",
    runningLabel: "Adding comments to the document...",
    completeLabel: "Added comments to the document",
    errorLabel: "Couldn't add comments",
    icon: PencilLineIcon
  },
  list_document_templates: {
    key: "word-templates",
    runningLabel: "Looking up your templates...",
    completeLabel: "Found your templates",
    errorLabel: "Couldn't list templates",
    icon: FileTextIcon
  },
  read_document_template: {
    key: "word-template-read",
    runningLabel: "Reading the template's styles...",
    completeLabel: "Read the template's styles",
    errorLabel: "Couldn't read the template",
    icon: FileTextIcon
  },
  pull_from_mytender: {
    key: "word-pull",
    runningLabel: "Pulling answers from mytender...",
    completeLabel: "Pulled answers from mytender",
    errorLabel: "Couldn't pull from mytender",
    icon: FileTextIcon
  },
  push_to_mytender: {
    key: "word-push",
    runningLabel: "Waiting for you to confirm the push...",
    completeLabel: "Push to mytender",
    errorLabel: "Couldn't push to mytender",
    icon: FileTextIcon
  },
  show_question: {
    key: "word-show-question",
    runningLabel: "Showing the question in the document...",
    completeLabel: "Showed the question in the document",
    errorLabel: "Couldn't show the question",
    icon: FileTextIcon
  },
  write_question_answer: {
    key: "word-write-answer",
    runningLabel: "Writing the answer into the document...",
    completeLabel: "Wrote the answer into the document",
    errorLabel: "Couldn't write the answer",
    icon: FileTextIcon
  },
  set_page_setup: {
    key: "word-page-setup",
    runningLabel: "Changing page setup...",
    completeLabel: "Changed page setup",
    errorLabel: "Couldn't change page setup",
    icon: FileTextIcon
  },
  apply_template: {
    key: "word-template-apply",
    runningLabel: "Applying the template to the document...",
    completeLabel: "Applied the template to the document",
    errorLabel: "Couldn't apply the template",
    icon: FileTextIcon
  },
  apply_template_styles: {
    key: "word-template-styles",
    runningLabel: "Applying the template's styles...",
    completeLabel: "Applied the template's styles",
    errorLabel: "Couldn't apply the template's styles",
    icon: FileTextIcon
  },
  insert_template_pages: {
    key: "word-template-pages",
    runningLabel: "Copying template pages into the document...",
    completeLabel: "Copied template pages into the document",
    errorLabel: "Couldn't copy the template pages",
    icon: FileTextIcon
  },
  apply_template_headers_footers: {
    key: "word-template-headers",
    runningLabel: "Applying the template's header and footer...",
    completeLabel: "Applied the template's header and footer",
    errorLabel: "Couldn't apply the header and footer",
    icon: FileTextIcon
  },
  format_document: {
    key: "word-format",
    runningLabel: "Formatting the document...",
    completeLabel: "Formatted the document",
    errorLabel: "Couldn't format the document",
    icon: PencilLineIcon
  },
  review_document_changes: {
    key: "word-review",
    runningLabel: "Reviewing tracked changes...",
    completeLabel: "Reviewed tracked changes",
    errorLabel: "Couldn't review tracked changes",
    icon: PencilLineIcon
  },
  insert_document_image: {
    key: "word-image",
    runningLabel: "Creating an image for the document...",
    completeLabel: "Inserted an image into the document",
    errorLabel: "Couldn't insert the image",
    icon: SparklesIcon
  },
  open_in_mytender: {
    key: "word-open-mytender",
    runningLabel: "Opening mytender...",
    completeLabel: "Opened mytender",
    errorLabel: "Couldn't open mytender",
    icon: CompassIcon
  }
} as const;

const GENERAL_PURPOSE_SUBAGENT_ACTIVITY: ToolActivity = {
  key: "helper-agent",
  runningLabel: "Running a helper agent...",
  completeLabel: "Finished helper agent",
  errorLabel: "Couldn't finish helper agent",
  icon: SparklesIcon
};

const getPartialJsonStringValue = (
  value: unknown,
  key: string
): string | null => {
  if (typeof value !== "string") return null;

  const pattern = new RegExp(
    String.raw`\\?["']${key}\\?["']\s*:\s*\\?["']([^"'\\]+)`
  );
  return pattern.exec(value)?.[1] ?? null;
};

const getTaskSubagentName = (part: ToolActivityPart): string | null => {
  if (part.toolName !== TASK_TOOL) return null;

  const args = asRecord(part.args);
  const parsedArgs = asRecord(parseMaybeJson(part.argsText));
  return (
    getStringValue(args, "subagent_type") ??
    getStringValue(parsedArgs, "subagent_type") ??
    getStringValue(args, "subagentType") ??
    getStringValue(parsedArgs, "subagentType") ??
    getPartialJsonStringValue(part.argsText, "subagent_type")
  );
};

const normalizeSubagentName = (value: string): string =>
  value.trim().toLowerCase().replace(/_/g, "-");

const READ_FILE_TOOL = "read_file";
const SKILL_PATH_PATTERN = /\/skills\/([^/]+)\//;

const getReadFilePath = (part: ToolActivityPart): string | null => {
  const args = asRecord(part.args);
  const parsedArgs = asRecord(parseMaybeJson(part.argsText));
  return (
    getStringValue(args, "file_path") ??
    getStringValue(parsedArgs, "file_path") ??
    getPartialJsonStringValue(part.argsText, "file_path")
  );
};

// A read against a bundled skill (e.g. /skills/product-knowledge/platform-reference.md)
// gets a friendly, specific label — "Reading product knowledge skill" — instead of the
// generic "Reading files..." that read_file falls back to.
const getSkillReadActivity = (part: ToolActivityPart): ToolActivity | null => {
  if (part.toolName !== READ_FILE_TOOL) return null;
  const path = getReadFilePath(part);
  if (!path) return null;
  const slug = SKILL_PATH_PATTERN.exec(path)?.[1];
  if (!slug) return null;
  const name = `${slug.replace(/-/g, " ")} skill`;
  return {
    key: `skill-read:${slug}`,
    runningLabel: `Reading ${name}...`,
    completeLabel: `Read ${name}`,
    errorLabel: `Couldn't read ${name}`,
    icon: BookOpenTextIcon
  };
};

const FULL_LIBRARY_FALLBACK_SCOPE = "full_library_fallback";
const ALL_FOLDERS_SCOPE = "all_folders";

const CONTENT_LIBRARY_FALLBACK_ACTIVITY: ToolActivity = {
  key: "content-library-fallback",
  runningLabel: "Searching whole library (no folders selected)...",
  completeLabel: "Searched whole library (no folders selected)",
  errorLabel: "Couldn't search your library",
  icon: BookOpenTextIcon
};

const CONTENT_LIBRARY_BID_LOOKUP_FAILED_ACTIVITY: ToolActivity = {
  key: "content-library-bid-lookup-failed",
  runningLabel: "Searching whole library...",
  completeLabel: "Searched whole library",
  errorLabel: "Couldn't search your library",
  icon: BookOpenTextIcon
};

const CONTENT_LIBRARY_ALL_FOLDERS_ACTIVITY: ToolActivity = {
  key: "content-library-all-folders",
  runningLabel: "Searching whole library (all folders)...",
  completeLabel: "Searched whole library (all folders)",
  errorLabel: "Couldn't search your library",
  icon: BookOpenTextIcon
};

const getContentLibraryScopeActivity = (
  part: ToolActivityPart
): ToolActivity | null => {
  if (part.toolName !== CONTENT_LIBRARY_TOOL) return null;
  const result = asRecord(parseMaybeJson(part.result));
  const scope = getStringValue(result, "scope");
  if (scope === FULL_LIBRARY_FALLBACK_SCOPE) {
    if (getStringValue(result, "reason") === "bid_lookup_failed") {
      return CONTENT_LIBRARY_BID_LOOKUP_FAILED_ACTIVITY;
    }
    return CONTENT_LIBRARY_FALLBACK_ACTIVITY;
  }
  if (scope === ALL_FOLDERS_SCOPE) {
    return CONTENT_LIBRARY_ALL_FOLDERS_ACTIVITY;
  }
  return null;
};

const getToolActivity = (
  partOrToolName: ToolActivityPart | string
): ToolActivity => {
  if (typeof partOrToolName !== "string") {
    const subagentName = getTaskSubagentName(partOrToolName);
    if (
      subagentName &&
      normalizeSubagentName(subagentName) === GENERAL_PURPOSE_SUBAGENT
    ) {
      return GENERAL_PURPOSE_SUBAGENT_ACTIVITY;
    }
    const skillActivity = getSkillReadActivity(partOrToolName);
    if (skillActivity) return skillActivity;
    const libraryScopeActivity = getContentLibraryScopeActivity(partOrToolName);
    if (libraryScopeActivity) return libraryScopeActivity;
  }

  const toolName =
    typeof partOrToolName === "string"
      ? partOrToolName
      : partOrToolName.toolName;
  return (
    ACTIVITY_BY_TOOL_NAME[toolName as keyof typeof ACTIVITY_BY_TOOL_NAME] ?? {
      key: `unknown:${toolName}`,
      runningLabel: "Working through your request...",
      completeLabel: "Worked through your request",
      errorLabel: "Couldn't work through your request",
      icon: SparklesIcon
    }
  );
};

const isRetrievalToolPart = (part: ToolActivityPart): boolean =>
  part.toolName === CONTENT_LIBRARY_TOOL || part.toolName === WEB_SEARCH_TOOL;

const isNavigationToolPart = (part: ToolActivityPart): boolean =>
  part.toolName === BID_NAVIGATION_TOOL_NAME;

type ToolActivityGroup = {
  key: string;
  activity: ToolActivity;
  parts: ToolActivityPart[];
};

// Collapse consecutive tool parts that resolve to the same activity into one
// group, so a burst of edits (e.g. several suggest_inline_revision calls in a
// row) renders a single "Prepared text revisions" row instead of one per edit.
// Grouping is by activity key, so tools that share a label (write_file /
// edit_file → "Updated files") also collapse. Only consecutive runs merge — an
// intervening different activity starts a fresh row. See MYT-1782.
const groupConsecutiveActivities = (
  parts: readonly ToolActivityPart[]
): ToolActivityGroup[] => {
  const groups: ToolActivityGroup[] = [];
  for (const part of parts) {
    const activity = getToolActivity(part);
    const last = groups[groups.length - 1];
    if (last && last.key === activity.key) {
      last.parts.push(part);
    } else {
      groups.push({ key: activity.key, activity, parts: [part] });
    }
  }
  return groups;
};

function BidNavigationReasonDropdown({
  part,
  children
}: {
  part: ToolActivityPart;
  children: (props: {
    open: boolean;
    hasReason: boolean;
    toggleOpen: () => void;
  }) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const request = useMemo(
    () =>
      parseBidNavigationRequest(part.result) ??
      parseBidNavigationRequest(part.args) ??
      parseBidNavigationRequest(part.argsText),
    [part.args, part.argsText, part.result]
  );
  const reason = request?.reason?.trim();
  const hasReason = Boolean(reason);
  const toggleOpen = () => {
    if (hasReason) setOpen((value) => !value);
  };

  return (
    <>
      {children({ open, hasReason, toggleOpen })}
      {open && reason ? (
        <ToolActivityDetail dataSlot="bid-navigation-reason">{reason}</ToolActivityDetail>
      ) : null}
    </>
  );
}

export function ToolActivityList({ indices }: { indices: readonly number[] }) {
  const messageParts = useAuiState((state) => state.message.parts);

  const toolParts = useMemo(() => {
    const parts: ToolActivityPart[] = [];
    for (const index of indices) {
      const part = messageParts[index];
      if (isToolActivityPart(part) && !HIDDEN_ACTIVITY_TOOL_NAMES.has(part.toolName)) {
        parts.push(part);
      }
    }
    return parts;
  }, [indices, messageParts]);

  const groups = useMemo(
    () => groupConsecutiveActivities(toolParts),
    [toolParts]
  );
  // A call still "running" once the thread has stopped never finished: show
  // it as failed rather than spinning forever.
  const stalled = useThreadStalled();
  const statusOf = (parts: readonly ToolActivityPart[]): ToolActivityStatus => {
    const status = getAggregateStatus(parts);
    return stalled && status === "running" ? "error" : status;
  };

  if (groups.length === 0) return null;

  return (
    <div
      data-slot="tool-activity-list"
      className="aui-tool-activity-list my-3 w-full"
    >
      {groups.map((group) => {
        const { activity, parts } = group;
        const status = statusOf(parts);
        const firstPart = parts[0];
        const groupKey = firstPart.toolCallId;

        // Navigation rows carry a per-call reason dropdown, so render each part
        // in the group individually rather than collapsing them into one row.
        if (isNavigationToolPart(firstPart)) {
          return (
            <div key={groupKey}>
              {parts.map((part) => (
                <BidNavigationReasonDropdown key={part.toolCallId} part={part}>
                  {({ open, hasReason, toggleOpen }) => (
                    <ToolActivityRow
                      runningLabel={activity.runningLabel}
                      completeLabel={activity.completeLabel}
                      errorLabel={activity.errorLabel}
                      icon={activity.icon}
                      status={statusOf([part])}
                      open={open}
                      hasDetails={hasReason}
                      onToggleSources={hasReason ? toggleOpen : undefined}
                    />
                  )}
                </BidNavigationReasonDropdown>
              ))}
            </div>
          );
        }

        // Memory rows expand into the entries, carry a decision on the right
        // when one is due, and collapse consecutive saves into one line.
        if (isMemoryActivityPart(firstPart)) {
          return (
            <div key={groupKey}>
              <MemoryActivityGroup parts={parts} activity={activity} status={status} />
            </div>
          );
        }

        // Retrieval rows collapse into one, merging sources across the group.
        if (isRetrievalToolPart(firstPart)) {
          const sources = getRetrievalSources(parts);
          return (
            <div key={groupKey}>
              <RetrievalSourcesDropdown sources={sources}>
                {({ open, sourceCount, toggleOpen }) => (
                  <ToolActivityRow
                    runningLabel={activity.runningLabel}
                    completeLabel={activity.completeLabel}
                    errorLabel={activity.errorLabel}
                    icon={activity.icon}
                    status={status}
                    open={open}
                    sourceCount={sourceCount}
                    onToggleSources={toggleOpen}
                  />
                )}
              </RetrievalSourcesDropdown>
            </div>
          );
        }

        return (
          <div key={groupKey}>
            <ToolActivityRow
              runningLabel={activity.runningLabel}
              completeLabel={activity.completeLabel}
              errorLabel={activity.errorLabel}
              icon={activity.icon}
              status={status}
            />
          </div>
        );
      })}
    </div>
  );
}

export { ToolActivityRow, getAggregateStatus, getToolActivity };
export type { ToolActivity, ToolActivityPart };
